/**
 * Influencer platforms — data-layer client.
 *
 * Thin typed wrapper over `apiClient` for the
 * `/api/v1/influencer-platforms` surface (feature #118). Mirrors the
 * contract declared in `apps/api/src/routes/influencer-platforms.ts`.
 *
 * Pure module (no React, no TanStack) so it can be reused from server-side
 * smoke harnesses and exercised directly in unit tests without a DOM.
 */

import { apiClient, ApiError } from "@/api-client";

// ---------------------------------------------------------------------------
// Platform enum — kept in lockstep with the API `PLATFORMS` tuple. The six
// supported channels for multi-platform influencer attribution.
// ---------------------------------------------------------------------------

export const PLATFORMS = [
	"instagram",
	"youtube",
	"tiktok",
	"twitter",
	"twitch",
	"facebook",
] as const;
export type Platform = (typeof PLATFORMS)[number];

export const PLATFORM_LABELS: Record<Platform, string> = {
	instagram: "Instagram",
	youtube: "YouTube",
	tiktok: "TikTok",
	twitter: "Twitter",
	twitch: "Twitch",
	facebook: "Facebook",
};

export function isPlatform(value: unknown): value is Platform {
	return (
		typeof value === "string" &&
		(PLATFORMS as readonly string[]).includes(value)
	);
}

// ---------------------------------------------------------------------------
// Attribution "kind" — the UI slices attributions into click vs conversion.
// The API endpoint itself only records the event (it does not persist a kind
// column), so the dash synthesizes a `click_id` that carries the kind prefix
// so the operator can differentiate rows when debugging.
// ---------------------------------------------------------------------------

export const ATTRIBUTION_KINDS = ["click", "conversion"] as const;
export type AttributionKind = (typeof ATTRIBUTION_KINDS)[number];

// ---------------------------------------------------------------------------
// Row / response types
// ---------------------------------------------------------------------------

export interface PlatformLinkRow {
	id: string;
	tenant_id: string;
	influencer_id: string;
	platform: Platform;
	handle: string;
	profile_url: string | null;
	created_at: number;
}

export interface AttributionRow {
	id: string;
	tenant_id: string;
	click_id: string;
	platform: Platform;
	campaign_id: string;
	influencer_id: string;
	ts: number;
}

export type AttributionCounts = Record<Platform, number>;

export interface AttributionReport {
	influencer_id: string;
	from: number | null;
	to: number | null;
	total: number;
	counts: AttributionCounts;
}

export interface LinksResponse {
	influencer_id: string;
	links: PlatformLinkRow[];
}

export interface CreateLinkResponse {
	link: PlatformLinkRow;
}

export interface AttributeResponse {
	attribution: AttributionRow;
}

// ---------------------------------------------------------------------------
// Validation helpers — pure functions, exported for unit tests.
//
// These mirror the server-side validators in `influencer-platforms.ts` so the
// dash surfaces identical error semantics before a round-trip.
// ---------------------------------------------------------------------------

// Identifiers: URL-safe, 1..64 chars.
const ID_RE = /^[a-zA-Z0-9_-]{1,64}$/;
// Handles: letters, digits, underscore, dot, dash — 1..64.
const HANDLE_RE = /^[a-zA-Z0-9_.\-]{1,64}$/;
const URL_MAX = 512;

export function isValidId(value: unknown): value is string {
	return typeof value === "string" && ID_RE.test(value);
}

/**
 * Normalises a handle the way the API does — strips any leading `@`
 * characters, trims whitespace, then enforces the handle regex. Returns
 * the cleaned handle, or `null` when the input cannot be normalised to a
 * valid handle.
 *
 * Examples:
 *   "@nis"     -> "nis"
 *   "  @nis "  -> "nis"
 *   "@@nis"    -> "nis"
 *   "nis"      -> "nis"
 *   ""         -> null
 *   "bad space"-> null
 */
export function normalizeHandle(raw: unknown): string | null {
	if (typeof raw !== "string") return null;
	const stripped = raw.trim().replace(/^@+/, "");
	if (stripped.length === 0) return null;
	if (!HANDLE_RE.test(stripped)) return null;
	return stripped;
}

/**
 * Validates a profile URL. Only http(s) protocols are allowed, total length
 * capped at 512 chars. Returns the normalised URL, or:
 *   - `undefined` when the input is empty/absent (column may be NULL)
 *   - `null` when the input is present but invalid
 */
export function validateProfileUrl(
	raw: unknown,
): string | null | undefined {
	if (raw === undefined || raw === null || raw === "") return undefined;
	if (typeof raw !== "string") return null;
	const trimmed = raw.trim();
	if (trimmed.length === 0) return undefined;
	if (trimmed.length > URL_MAX) return null;
	let parsed: URL;
	try {
		parsed = new URL(trimmed);
	} catch {
		return null;
	}
	if (parsed.protocol !== "http:" && parsed.protocol !== "https:") return null;
	return parsed.toString();
}

/**
 * Revenue in cents. Rejects negatives, non-integers, and NaN. Returns:
 *   - `undefined` for empty input
 *   - a non-negative integer for valid input
 *   - `null` for invalid input
 */
export function parseRevenueCents(raw: unknown): number | null | undefined {
	if (raw === undefined || raw === null || raw === "") return undefined;
	if (typeof raw === "number") {
		if (!Number.isFinite(raw) || !Number.isInteger(raw) || raw < 0) return null;
		return raw;
	}
	if (typeof raw !== "string") return null;
	const trimmed = raw.trim();
	if (trimmed.length === 0) return undefined;
	if (!/^\d+$/.test(trimmed)) return null;
	const n = Number(trimmed);
	if (!Number.isFinite(n) || !Number.isInteger(n) || n < 0) return null;
	return n;
}

// ---------------------------------------------------------------------------
// Attribution window helpers
// ---------------------------------------------------------------------------

/**
 * Renders a YYYY-MM-DD HTML date-input into a Unix-seconds timestamp.
 * Returns `null` for invalid / empty input.
 */
export function dateInputToUnix(raw: unknown, endOfDay = false): number | null {
	if (typeof raw !== "string" || raw.trim().length === 0) return null;
	if (!/^\d{4}-\d{2}-\d{2}$/.test(raw)) return null;
	const suffix = endOfDay ? "T23:59:59Z" : "T00:00:00Z";
	const ms = Date.parse(`${raw}${suffix}`);
	if (!Number.isFinite(ms)) return null;
	return Math.floor(ms / 1000);
}

// ---------------------------------------------------------------------------
// Attribution-tester → API payload bridge.
//
// The spec-driven UI surface is `{influencer_id, platform, kind,
// revenue_cents?}`, but the API contract is `{click_id, platform,
// influencer_id, campaign_id}`. We synthesize a click_id from the kind plus
// a timestamp + random suffix so records remain traceable.
// ---------------------------------------------------------------------------

export interface AttributionInput {
	influencer_id: string;
	platform: Platform;
	kind: AttributionKind;
	revenue_cents?: number;
	/** Optional — defaults to the dash-synthesised sentinel "default". */
	campaign_id?: string;
	/** Optional click id — when omitted the UI synthesises one. */
	click_id?: string;
}

export interface ApiAttributionPayload {
	click_id: string;
	platform: Platform;
	campaign_id: string;
	influencer_id: string;
}

function randomSuffix(): string {
	const g = globalThis as { crypto?: { randomUUID?: () => string } };
	if (typeof g.crypto?.randomUUID === "function") {
		return g.crypto.randomUUID().replace(/-/g, "").slice(0, 10);
	}
	return Math.random().toString(36).slice(2, 12);
}

export function buildAttributionPayload(
	input: AttributionInput,
	now: number = Math.floor(Date.now() / 1000),
): ApiAttributionPayload {
	const seed = `${input.kind}_${now}_${randomSuffix()}`
		.replace(/[^a-zA-Z0-9_-]/g, "")
		.slice(0, 60);
	return {
		click_id: input.click_id ?? seed,
		platform: input.platform,
		campaign_id: input.campaign_id?.trim() || "default",
		influencer_id: input.influencer_id,
	};
}

// ---------------------------------------------------------------------------
// Form schema — hand-rolled `safeParse` surface mirroring the os-targeting
// pattern. Keeps the form component dep-free and testable.
// ---------------------------------------------------------------------------

export interface ParseOk<T> {
	success: true;
	data: T;
}
export interface ParseErr {
	success: false;
	errors: Record<string, string>;
}
export type ParseResult<T> = ParseOk<T> | ParseErr;

export interface LinkFormInput {
	influencer_id: string;
	platform: string;
	handle: string;
	profile_url?: string;
}

export interface LinkFormParsed {
	influencer_id: string;
	platform: Platform;
	handle: string;
	profile_url: string | null;
}

export const linkFormSchema = {
	safeParse(input: LinkFormInput): ParseResult<LinkFormParsed> {
		const errors: Record<string, string> = {};

		const influencerId = (input.influencer_id ?? "").trim();
		if (influencerId.length === 0) {
			errors.influencer_id = "Influencer ID is required.";
		} else if (!ID_RE.test(influencerId)) {
			errors.influencer_id =
				"Influencer ID may only contain letters, digits, dashes, or underscores.";
		}

		if (!isPlatform(input.platform)) {
			errors.platform = "Pick a platform.";
		}

		const handle = normalizeHandle(input.handle);
		if (!handle) {
			errors.handle =
				"Handle is required and may only contain letters, digits, dashes, dots, or underscores.";
		}

		const url = validateProfileUrl(input.profile_url);
		if (url === null) {
			errors.profile_url = "Profile URL must be a valid http(s) link.";
		}

		if (Object.keys(errors).length > 0) return { success: false, errors };
		return {
			success: true,
			data: {
				influencer_id: influencerId,
				platform: input.platform as Platform,
				handle: handle as string,
				profile_url: url ?? null,
			},
		};
	},
};

export interface AttributionFormInput {
	influencer_id: string;
	platform: string;
	kind: string;
	revenue_cents?: string;
	campaign_id?: string;
}

export interface AttributionFormParsed {
	influencer_id: string;
	platform: Platform;
	kind: AttributionKind;
	revenue_cents: number | null;
	campaign_id: string;
}

export const attributionFormSchema = {
	safeParse(
		input: AttributionFormInput,
	): ParseResult<AttributionFormParsed> {
		const errors: Record<string, string> = {};

		const influencerId = (input.influencer_id ?? "").trim();
		if (influencerId.length === 0) {
			errors.influencer_id = "Influencer ID is required.";
		} else if (!ID_RE.test(influencerId)) {
			errors.influencer_id = "Influencer ID contains invalid characters.";
		}

		if (!isPlatform(input.platform)) {
			errors.platform = "Pick a platform.";
		}

		if (!(ATTRIBUTION_KINDS as readonly string[]).includes(input.kind)) {
			errors.kind = "Pick a kind.";
		}

		const revenue = parseRevenueCents(input.revenue_cents);
		if (revenue === null) {
			errors.revenue_cents =
				"Revenue must be a whole number of cents (>= 0) or empty.";
		}

		const rawCampaign = (input.campaign_id ?? "").trim();
		let campaignId = "default";
		if (rawCampaign.length > 0) {
			if (!ID_RE.test(rawCampaign)) {
				errors.campaign_id = "Campaign ID contains invalid characters.";
			} else {
				campaignId = rawCampaign;
			}
		}

		if (Object.keys(errors).length > 0) return { success: false, errors };
		return {
			success: true,
			data: {
				influencer_id: influencerId,
				platform: input.platform as Platform,
				kind: input.kind as AttributionKind,
				revenue_cents: revenue ?? null,
				campaign_id: campaignId,
			},
		};
	},
};

// ---------------------------------------------------------------------------
// Remote operations — bare fetch wrappers. TanStack Query consumers import
// these directly.
// ---------------------------------------------------------------------------

const BASE = "/influencer-platforms";

export interface CreateLinkArgs {
	influencer_id: string;
	platform: Platform;
	handle: string;
	profile_url?: string | null;
}

export async function createPlatformLink(
	args: CreateLinkArgs,
): Promise<PlatformLinkRow> {
	const res = await apiClient.post<CreateLinkResponse>(BASE, {
		influencer_id: args.influencer_id,
		platform: args.platform,
		handle: args.handle,
		profile_url: args.profile_url ?? null,
	});
	return res.link;
}

export async function deletePlatformLink(id: string): Promise<void> {
	try {
		await apiClient.delete(`${BASE}/${encodeURIComponent(id)}`);
	} catch (err) {
		if (err instanceof ApiError && err.status === 404) return;
		throw err;
	}
}

export async function fetchLinksByInfluencer(
	influencerId: string,
): Promise<PlatformLinkRow[]> {
	const res = await apiClient.get<LinksResponse>(
		`${BASE}/by-influencer/${encodeURIComponent(influencerId)}`,
	);
	return res.links;
}

export async function recordAttribution(
	input: AttributionInput,
): Promise<AttributionRow> {
	const payload = buildAttributionPayload(input);
	const res = await apiClient.post<AttributeResponse>(
		`${BASE}/attribute`,
		payload,
	);
	return res.attribution;
}

export interface AttributionWindow {
	from?: number | null;
	to?: number | null;
}

export async function fetchAttributionReport(
	influencerId: string,
	window: AttributionWindow = {},
): Promise<AttributionReport> {
	const query: Record<string, string> = {};
	if (window.from !== null && window.from !== undefined) {
		query.from = String(window.from);
	}
	if (window.to !== null && window.to !== undefined) {
		query.to = String(window.to);
	}
	return apiClient.get<AttributionReport>(
		`${BASE}/attribution/${encodeURIComponent(influencerId)}`,
		{ query },
	);
}

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

export const influencerPlatformsQueryKeys = {
	all: ["influencer-platforms"] as const,
	linksByInfluencer: (id: string) =>
		[...influencerPlatformsQueryKeys.all, "links", id] as const,
	attribution: (id: string, window: AttributionWindow) =>
		[
			...influencerPlatformsQueryKeys.all,
			"attribution",
			id,
			window.from ?? null,
			window.to ?? null,
		] as const,
};

// ---------------------------------------------------------------------------
// Bar chart helper — maps counts to layout segments. Pure so it can be
// unit-tested alongside the validators.
// ---------------------------------------------------------------------------

export interface BarSegment {
	platform: Platform;
	label: string;
	count: number;
	percent: number;
}

export function buildBarSegments(counts: AttributionCounts): BarSegment[] {
	const total = PLATFORMS.reduce((sum, p) => sum + (counts[p] ?? 0), 0);
	return PLATFORMS.map((platform) => {
		const count = counts[platform] ?? 0;
		const percent = total === 0 ? 0 : (count / total) * 100;
		return {
			platform,
			label: PLATFORM_LABELS[platform],
			count,
			percent,
		};
	});
}
